import {
  Alert,
  AlertDescription,
  AlertIcon,
  HStack,
  Heading,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

import { Bank } from '@finley-technologies/finley-types';
import {
  ValleyBankSubLoanAccount,
  getSubLoansTotalOutstandingBalance,
  isContributesToOutstandingBalance,
  ValleyBankLoanAccount,
} from '@finley-technologies/valley-bank-utils';

import { CsvXlsxDownloadSelect } from 'packages/common-frontend/components/CsvXlsxDownloadSelect';
import { Icon } from 'packages/common-frontend/components/icons';
import { InfoCard } from 'packages/common-frontend/components/InfoCard';
import {
  formatCurrencyAmount,
  percentageToPercentageLabel,
  toDashOnNullish,
} from 'packages/common-frontend/utils';
import { LoanAccountSummaryLink } from 'valleyBank/domains/LoanAccounts/hooks/LoanAccountContext';

const loanSummaryDataKeyToHeaderMap = {
  contributesToOutstandingBalance: 'Contributes to Outstanding Balance',
  productDescription: 'Product Name',
  customerType: 'Customer Type',
  commitmentId: 'Commitment ID',
  noteId: 'Note ID',
  loanAmount: 'Loan Amount',
  currentBalance: 'Current Balance',
  noteDate: 'Note Date',
  maturityDate: 'Maturity Date',
  interestRate: 'Interest Rate',
  currency: 'Currency',
  fxAmount: 'FX Amount',
  fxRate: 'FX Rate',
  source: 'Source',
};

const loanSummaryHeaders = _.map(
  loanSummaryDataKeyToHeaderMap,
  (header, key) => ({
    label: header,
    key,
  }),
);

const getIdWithoutLoanAccoutNumber = ({
  loanAccountNumber,
  id,
}: {
  loanAccountNumber: string;
  id: string | null;
}): string | null => {
  if (!id) return id;
  const regex = new RegExp(`^${loanAccountNumber}(.*)`, 'g');
  const capturingGroup = regex.exec(id)?.[1];
  return capturingGroup ?? id;
};

const CommitmentOrNoteIdWithLoanAccountLink: React.FC<{
  loanAccountNumber: string;
  id: string;
}> = ({ loanAccountNumber, id }) => {
  const idWithoutLoanAccountNumber = getIdWithoutLoanAccoutNumber({
    loanAccountNumber,
    id,
  });
  return (
    <>
      <LoanAccountSummaryLink loanAccountId={loanAccountNumber}>
        {loanAccountNumber}
      </LoanAccountSummaryLink>
      {`-${idWithoutLoanAccountNumber}`}
    </>
  );
};

export const LoansTable: React.FC<{
  loans: ValleyBankSubLoanAccount[];
  noLoansMessage: string;
  title?: string;
  showStats?: boolean;
  cifNumber?: string | null;
  loanAccountLoanPartyMap?: {
    [loanAccountNumber: string]: {
      [cifNumber: string]: ValleyBankLoanAccount['loanParties'][number];
    };
  };
}> = ({
  loans,
  noLoansMessage,
  title,
  showStats = true,
  cifNumber,
  loanAccountLoanPartyMap,
}) => {
  const loanTableData = _.map(loans, (loan) => {
    const {
      accountNumber: loanAccountNumber,
      commitmentId,
      noteId,
      productDescription,
      loanAmount,
      noteDate,
      maturityDate,
      interestRate,
      currency,
      fxAmount,
      fxRate,
      currentBalance,
      source,
    } = loan;

    const customerType =
      loanAccountLoanPartyMap && cifNumber
        ? loanAccountLoanPartyMap[loanAccountNumber][cifNumber]
            .rel_code_description
        : undefined;

    return {
      contributesToOutstandingBalance: isContributesToOutstandingBalance(loan),
      productDescription,
      customerType,
      commitmentId,
      noteId,
      loanAmount,
      currentBalance,
      noteDate,
      maturityDate,
      interestRate,
      currency,
      fxAmount,
      fxRate,
      source,
    };
  });
  return (
    <VStack w="100%" spacing="24px" alignItems="flex-start">
      {title && (
        <Heading as="h2" fontSize="24px" mb="24px">
          {title}
        </Heading>
      )}
      {loans.length === 0 && (
        <Alert status="info">
          <AlertIcon />
          <AlertDescription>{noLoansMessage}</AlertDescription>
        </Alert>
      )}
      {loans.length > 0 && (
        <>
          {showStats && (
            <HStack spacing="24px" w="100%">
              <InfoCard
                title="Total Outstanding Balance"
                value={formatCurrencyAmount(
                  getSubLoansTotalOutstandingBalance(loans),
                  Bank.Currency.USD,
                )}
                icon={<Icon icon="dollar" size="24px" />}
              />
            </HStack>
          )}
          {/* @jendevelops TODO implement search and show/hide columns */}
          {/* <HStack w="100%" spacing="16px" justifyContent="flex-end">
        <Button variant="outline" colorScheme="aurora">
          Show/Hide Columns
        </Button>
        <Button
          variant="outline"
          colorScheme="aurora"
          leftIcon={<Icon icon="search" />}
        >
          Search
        </Button>
      </HStack> */}
          <CsvXlsxDownloadSelect
            dataRows={loanTableData}
            headers={loanSummaryHeaders}
            filenamePrefix={`loan-account-${loans[0].accountNumber}-loan-summary`}
          />
          <TableContainer w="100%" overflowX="scroll">
            <Table>
              <Thead>
                <Tr>
                  {_.map(loanSummaryDataKeyToHeaderMap, (header) => {
                    if (
                      header === 'Customer Type' &&
                      (!cifNumber || !loanAccountLoanPartyMap)
                    ) {
                      return undefined;
                    }

                    return <Th>{header}</Th>;
                  })}
                </Tr>
              </Thead>
              <Tbody>
                {loans.map((loan) => {
                  const {
                    accountNumber: loanAccountNumber,
                    commitmentId,
                    noteId,
                    productDescription,
                    loanAmount,
                    noteDate,
                    maturityDate,
                    interestRate,
                    currency,
                    fxAmount,
                    fxRate,
                    currentBalance,
                    source,
                    type,
                  } = loan;

                  return (
                    <Tr
                      key={`${type}-${loanAccountNumber}-${commitmentId}-${noteId}`}
                    >
                      <Td textAlign="center">
                        {isContributesToOutstandingBalance(loan) && (
                          <Icon icon="check" />
                        )}
                      </Td>

                      <Td>{toDashOnNullish(productDescription)}</Td>
                      {cifNumber && loanAccountLoanPartyMap && (
                        <Td>
                          {toDashOnNullish(
                            loanAccountLoanPartyMap[loanAccountNumber][
                              cifNumber
                            ].rel_code_description,
                          )}
                        </Td>
                      )}
                      <Td>
                        {commitmentId ? (
                          <CommitmentOrNoteIdWithLoanAccountLink
                            loanAccountNumber={loanAccountNumber}
                            id={commitmentId}
                          />
                        ) : (
                          '-'
                        )}
                      </Td>
                      {/* @jendevelops TODO add collateral page link */}
                      <Td>
                        {noteId ? (
                          <CommitmentOrNoteIdWithLoanAccountLink
                            loanAccountNumber={loanAccountNumber}
                            id={noteId}
                          />
                        ) : (
                          '-'
                        )}
                      </Td>
                      <Td>
                        {formatCurrencyAmount(
                          loanAmount ?? 0,
                          Bank.Currency.USD,
                        )}
                      </Td>
                      <Td>
                        {currentBalance
                          ? formatCurrencyAmount(
                              currentBalance,
                              Bank.Currency.USD,
                            )
                          : '-'}
                      </Td>
                      <Td>{toDashOnNullish(noteDate)}</Td>
                      <Td>{toDashOnNullish(maturityDate)}</Td>
                      <Td>
                        {interestRate
                          ? percentageToPercentageLabel(interestRate * 100, 2)
                          : '-'}
                      </Td>
                      <Td>{toDashOnNullish(currency)}</Td>
                      <Td>{toDashOnNullish(fxAmount)}</Td>
                      <Td>{toDashOnNullish(fxRate)}</Td>
                      <Td>{source}</Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </>
      )}
    </VStack>
  );
};
